import { GetSessionCookie } from "@/services/Cookies/FirebaseSession";
import {
	classifyFrontendOutcome,
	createFrontendRequestId,
	emitFrontendApiTelemetry,
	getFrontendObservabilityHeaders,
} from "@/lib/observability/frontendTelemetry";

type Headers = { [key: string]: string };

type RequestConfig = {
	responseType?: string;
	headers?: Headers;
	params?: Object;
	withApiKey?: boolean;
	next?: any;
	cache?: RequestCache;
};

interface ApiInstanceOptions {
	controller: string;
	headers?: Headers;
	debug?: boolean; // Add debug option
	customBaseUrl?: string; // Add custom base URL option
	customApiKey?: string; // Add custom API key option
}

export interface ApiResponse<T> {
	ok: boolean;
	data?: T;
	status: number;
	statusText: string;
	headers?: globalThis.Headers;
	error?: string;
	message?: string;
}

const BACKEND_BASE_URL = process.env.NEXT_PUBLIC_BACKEND!;
const BACKEND_API_KEY = process.env.BACKEND_API_KEY!;

function objectToURLSearchParams(obj: Record<string, any>): URLSearchParams {
	const params = new URLSearchParams();

	if (!obj) return params; // Return empty params if obj is null or undefined

	Object.entries(obj).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}

		// Check if value is an array or object, then stringify it for correct encoding
		if (Array.isArray(value)) {
			value
				.filter((val) => val !== undefined && val !== null && val !== "")
				.forEach((val) => params.append(key, String(val)));
		} else if (typeof value === "object" && value !== null) {
			params.append(key, JSON.stringify(value)); // Stringify nested objects
		} else {
			params.append(key, String(value)); // Append other values directly
		}
	});

	return params;
}

// Removes trailing or leading slashes from the URL
function cleanSlashesFromUrl(controller: string): string {
	return controller.replace(/\/+$/, "").replace(/^\/+/, "");
}

export function createBackendApiInstance(options: ApiInstanceOptions) {
	const { controller, headers: defaultHeaders, debug = false, customBaseUrl, customApiKey } = options;
	const cleanController = cleanSlashesFromUrl(controller);

	const buildUrl = (endpoint: string) => {
		const baseUrl = customBaseUrl || BACKEND_BASE_URL;
		const cleanEndpoint = cleanSlashesFromUrl(endpoint);
		const constructedUrl = cleanEndpoint !== "" ? `${baseUrl}${cleanController}/${cleanEndpoint}` : `${baseUrl}${cleanController}`;

		// if (debug) {
		// 	console.log(`🔗 Building URL:`, {
		// 		baseUrl: baseUrl,
		// 		controller: cleanController,
		// 		endpoint: cleanEndpoint,
		// 		constructedUrl,
		// 	});
		// }

		return constructedUrl;
	};

	const request = async <T = any>(method: string, endpoint: string, data?: any, requestConfig?: RequestConfig): Promise<ApiResponse<T>> => {
		const startTime = Date.now();
		const token = await GetSessionCookie();
		const requestId = createFrontendRequestId();

		const url = buildUrl(endpoint);

		const queryString = objectToURLSearchParams(requestConfig?.params).toString();
		const finalUrl = queryString ? `${url}?${queryString}` : url;

		// Performance logging for debugging
		if (debug) {
			console.log(`🚀 ${method} Request Started:`, {
				endpoint: finalUrl,
				controller: cleanController,
				hasAuth: !!token,
				hasApiKey: !!requestConfig?.withApiKey,
				timestamp: new Date().toISOString(),
			});
		}

		const headers: Headers = {
			// "x-api-key": BACKEND_API_KEY,
			"Content-Type": "application/json",
			Authorization: token ? `Bearer ${token}` : undefined,
			...getFrontendObservabilityHeaders(requestId),
			...defaultHeaders,
			...requestConfig?.headers,
		};

		if (requestConfig?.withApiKey) {
			headers["x-api-key"] = customApiKey || BACKEND_API_KEY;
		}

		const options: RequestInit = {
			method,
			headers: headers as HeadersInit,
			...(data && { body: JSON.stringify(data) }),
		};

		try {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 30000); // Reduced from 5 to 30 seconds
			const response = await fetch(finalUrl, {
				...options,
				next: requestConfig?.next,
				cache: requestConfig?.cache,
				signal: controller.signal,
			});
			clearTimeout(timeout);

			const responseTime = Date.now() - startTime;

			// Performance logging
			if (debug || responseTime > 1000) {
				// Log slow requests
				// console.log(`📊 ${method} Response:`, {
				// 	endpoint: finalUrl,
				// 	status: response.status,
				// 	responseTime: `${responseTime}ms`,
				// 	size: response.headers.get("content-length") || "unknown",
				// });
			}

			if (!response.ok) {
				let errorMessage = response.statusText || "Request failed";
				const contentType = response.headers.get("content-type") || "";
				const responseText = await response.text();

				// Try to extract message from JSON response if it's JSON
				if (contentType.includes("application/json") && responseText.trim() !== "") {
					try {
						const jsonError = JSON.parse(responseText);
						errorMessage = jsonError.message || jsonError.error || responseText;
					} catch {
						// If JSON parsing fails, use the raw text
						errorMessage = responseText || response.statusText || "Request failed";
					}
				} else if (responseText.trim() !== "") {
					// Use raw text if not JSON
					errorMessage = responseText;
				}

				if (data) console.log("Request Data:", JSON.stringify(data, null, 2), responseText);

				await emitFrontendApiTelemetry({
					requestId,
					targetService: "hta-backend",
					method,
					endpoint: finalUrl,
					statusCode: response.status,
					outcome: classifyFrontendOutcome(response.status),
					durationMs: responseTime,
				});

				return {
					ok: false,
					status: response.status,
					statusText: response.statusText,
					error: `${method} ${cleanController}/${cleanSlashesFromUrl(endpoint)}`,
					message: errorMessage,
				};
			} else if (response.status === 204) {
				await emitFrontendApiTelemetry({
					requestId,
					targetService: "hta-backend",
					method,
					endpoint: finalUrl,
					statusCode: response.status,
					outcome: classifyFrontendOutcome(response.status),
					durationMs: responseTime,
				});

				// No content response
				return {
					ok: true,
					status: response.status,
					statusText: response.statusText,
					data: undefined,
					headers: response.headers,
					error: undefined,
					message: undefined,
				};
			}

			await emitFrontendApiTelemetry({
				requestId,
				targetService: "hta-backend",
				method,
				endpoint: finalUrl,
				statusCode: response.status,
				outcome: classifyFrontendOutcome(response.status),
				durationMs: responseTime,
			});

			let responseData: T | any;
			const type = requestConfig?.responseType?.toLowerCase?.() ?? "json";
			const contentType = response.headers.get("content-type") || "";

			switch (type) {
				case "text":
					responseData = await response.text();
					break;
				case "blob":
					responseData = await response.blob();
					break;
				case "arraybuffer":
					responseData = await response.arrayBuffer();
					break;
				default:
					// Enhanced JSON parsing with fallback
					const textResponse = await response.text();

					if (textResponse.trim() === "") {
						responseData = null;
					} else if (contentType.includes("application/json")) {
						try {
							responseData = JSON.parse(textResponse);
						} catch (jsonError) {
							console.warn(`⚠️ JSON parsing failed for ${method} ${finalUrl}:`, {
								contentType,
								responseText: textResponse,
								error: jsonError instanceof Error ? jsonError.message : jsonError,
							});
							// Return the raw text response when JSON parsing fails
							responseData = textResponse;
						}
					} else {
						// Non-JSON content type, return as text
						responseData = textResponse;
					}
					break;
			}

			return {
				ok: response.ok,
				data: responseData,
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
				error: undefined,
				message: undefined,
			};
		} catch (error) {
			const responseTime = Date.now() - startTime;

			// Always log errors with timing
			// console.error(`❌ ${method} Request Failed:`, {
			// 	endpoint: finalUrl,
			// 	controller: cleanController,
			// 	responseTime: `${responseTime}ms`,
			// 	error: error instanceof Error ? error.message : error,
			// });

			let errorMessage: string;
			let statusText: string;
			let statusCode: number;

			if (error instanceof DOMException && error.name === "AbortError") {
				statusCode = 408; // Request Timeout
				statusText = "Request Timeout";
				errorMessage = `Request timeout after 30 seconds for ${method} ${finalUrl}`;
			} else if (error instanceof TypeError && error.message.includes("fetch")) {
				statusCode = 0; // Network Error (no connection)
				statusText = "Network Error";
				errorMessage = `Network error connecting to ${finalUrl}: ${error.message}`;
			} else if (error instanceof Error) {
				statusCode = 500; // Internal Server Error for other errors
				statusText = "Request Error";
				errorMessage = `${method} request to ${finalUrl} failed: ${error.message}`;
			} else {
				statusCode = 500; // Internal Server Error for unknown errors
				statusText = "Unknown Error";
				errorMessage = `Unknown error occurred during ${method} request to ${finalUrl}`;
			}

			await emitFrontendApiTelemetry({
				requestId,
				targetService: "hta-backend",
				method,
				endpoint: finalUrl,
				statusCode,
				outcome: statusCode === 0 ? "network_error" : classifyFrontendOutcome(statusCode),
				durationMs: responseTime,
			});

			return {
				ok: false,
				status: statusCode,
				statusText,
				error: `${method} ${cleanController}/${cleanSlashesFromUrl(endpoint)}`,
				message: errorMessage,
			};
		}
	};

	return {
		get: <T = any>(endpoint: string, requestConfig?: RequestConfig) => request<T>("GET", endpoint, undefined, requestConfig),

		post: <T = any>(endpoint: string, data?: any, requestConfig?: RequestConfig) => request<T>("POST", endpoint, data, requestConfig),

		put: <T = any>(endpoint: string, data?: any, requestConfig?: RequestConfig) => request<T>("PUT", endpoint, data, requestConfig),

		delete: <T = any>(endpoint: string, requestConfig?: RequestConfig) => request<T>("DELETE", endpoint, undefined, requestConfig),

		patch: <T = any>(endpoint: string, data?: any, requestConfig?: RequestConfig) => request<T>("PATCH", endpoint, data, requestConfig),
	};
}

// Add debug logging to transformResponse if it exists
// If this file doesn't exist, you can ignore this part
// If this file doesn't exist, you can ignore this part
